// src/components/POSBilling.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import BranchSelector from './BranchSelector';
import ReactDOM from 'react-dom/client';

const API_URL = '/api';

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

const roundToTwo = (num) => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

const getPriceWithVAT = (basePrice, vatRate) => {
  return roundToTwo(basePrice * (1 + vatRate / 100));
};

const getVATAmount = (total, vatRate) => {
  return roundToTwo(total * (vatRate / (100 + vatRate)));
};

const getSubtotalExclVAT = (total, vatRate) => {
  return roundToTwo(total - getVATAmount(total, vatRate));
};

const transliterateToArabic = (englishText) => {
  if (!englishText || englishText === 'Guest') return 'ضيف';
  
  const text = englishText.toLowerCase().trim();
  
  const specialMap = {
    'sh': 'ش', 'ch': 'تش', 'th': 'ث', 'kh': 'خ', 'gh': 'غ',
    'dh': 'ذ', 'zh': 'ژ', 'ph': 'ف', 'ng': 'نغ', 'nk': 'نك',
    'nd': 'ند', 'nt': 'نت', 'mp': 'مب', 'mb': 'مب'
  };

  const letterMap = {
    'a': 'ا', 'b': 'ب', 'c': 'ك', 'd': 'د', 'e': 'ي', 'f': 'ف',
    'g': 'ج', 'h': 'ه', 'i': 'ي', 'j': 'ج', 'k': 'ك', 'l': 'ل',
    'm': 'م', 'n': 'ن', 'o': 'و', 'p': 'ب', 'q': 'ق', 'r': 'ر',
    's': 'س', 't': 'ت', 'u': 'و', 'v': 'ف', 'w': 'و', 'x': 'كس',
    'y': 'ي', 'z': 'ز'
  };

  let result = '';
  let i = 0;

  while (i < text.length) {
    let matched = false;
    
    for (let len = 3; len >= 2; len--) {
      if (i + len <= text.length) {
        const sub = text.substring(i, i + len);
        if (specialMap[sub]) {
          result += specialMap[sub];
          i += len;
          matched = true;
          break;
        }
      }
    }
    
    if (!matched) {
      const char = text[i];
      result += letterMap[char] || char;
      i++;
    }
  }

  return result;
};

const getValueOrNA = (value) => {
  return value && value.trim() !== '' ? value : 'Not available';
};

const paymentOptions = [
  { value: 'Cash', label: 'Cash', icon: '💵' },
  { value: 'Card', label: 'Card', icon: '💳' },
  { value: 'Other', label: 'Other', icon: '📱' },
  { value: 'Bank Transfer', label: 'Bank Transfer', icon: '🏦' }
];

const statusOptions = [
  { value: 'Delivery Pending', label: 'Delivery Pending', color: '#f59e0b', bg: '#fef3c7' },
  { value: 'Out for Delivery', label: 'Out for Delivery', color: '#8b5cf6', bg: '#ede9fe' },
  { value: 'Delivered', label: 'Delivered', color: '#10b981', bg: '#d1fae5' },
  { value: 'Cancelled', label: 'Cancelled', color: '#ef4444', bg: '#fee2e2' }
];

// ============================================================
// COMPONENT: ThermalBillPrint
// ============================================================

const ThermalBillPrint = React.forwardRef(({ bill, branch, vatRate, deliveryType }, ref) => {
  const subtotal = Number(bill.subtotal || bill.total_amount || 0);
  const actualVatRate = bill.vat_rate || vatRate || 5;
  let vatAmount = Number(bill.vat || bill.vat_amount || 0);
  let total = Number(bill.final_amount || bill.grand_total || 0);
  
  if (vatAmount === 0 && subtotal > 0) {
    vatAmount = subtotal * (actualVatRate / 100);
    total = subtotal + vatAmount;
  }
  
  const date = new Date(bill.created_at || Date.now()).toLocaleString();
//const logoUrl = '/nursery/logo.png';
  const logoUrl = 'https://nasmatalzahour.com/nursery/logo.png';
//const logoUrl = window.location.origin + '/logo.png';
  const items = bill.items || bill.bill_items || [];

  const customerNameEnglish = bill.customer_name || 'Guest';
  const customerNameArabic = transliterateToArabic(customerNameEnglish);
  const trnNumber = branch?.trn_no || 'TRN: 100220990400003';

  return (
    <div ref={ref} className="thermal-printable-area" style={{
      fontFamily: "monospace", 
      width: '70mm',
      margin: '0 auto',
      padding: '0px 2px',
      fontSize: '12px', 
      fontWeight: 'normal',
      lineHeight: '1.3',
      letterSpacing: '0px', 
      boxSizing: 'border-box',
      color: '#000000'
    }}>
      
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        marginBottom: '8px', 
        paddingBottom: '8px', 
        borderBottom: '1px dashed #000',
        gap: '10px'
      }}>
        <img 
          src={logoUrl} 
          alt="Logo" 
          style={{ width: '45px', height: 'auto', filter: 'grayscale(100%)' }} 
          onError={(e) => e.target.style.display = 'none'} 
        />
        
        <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', lineHeight: '1.1' }}>NASMAT</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', lineHeight: '1.1' }}>AL ZOHOUR</div>
          <div style={{ fontSize: '9px', color: '#000000', fontWeight: 'normal', lineHeight: '1.2', marginTop: '2px', maxWidth: '180px', wordBreak: 'break-word' }}>
            {branch?.address || ''}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>TAX INVOICE</div>
      <div style={{ textAlign: 'center', fontSize: '11px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px dotted #ccc' }}>
        <span style={{ fontWeight: 'bold' }}>📞</span> +971552698951 / +971526629183
      </div>
      
      <div style={{ marginBottom: '8px', fontSize: '11px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <span>Bill No: {bill.bill_number}</span>
          <span style={{ fontWeight: 'bold', textAlign: 'right' }}>Date: {date.split(',')[0]}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '2px' }}>
          <span style={{ color:'#000000', textAlign: 'right' }}>{trnNumber}</span>
        </div>
        <div style={{ marginTop: '2px' }}>
          <span>Customer: </span>
          <span style={{ fontWeight: 'bold' }}>
            {customerNameEnglish}
            {customerNameArabic && ` / ${customerNameArabic}`}
          </span>
        </div>
        
        {bill.customer_phone && <div>Phone: {bill.customer_phone}</div>}
        {bill.customer_address && <div>Address: {bill.customer_address.substring(0, 30)}</div>}
        <div>Payment: {bill.payment_mode}</div>
        <div>Delivery: {bill.delivery_type === 'delivery' ? ' Home Delivery' : 'Walk-in'}</div>
      </div>

      <div style={{ display: 'table', width: '100%', borderTop: '1px solid #000', borderBottom: '1px solid #000', padding: '4px 0', fontWeight: 'bold' }}>
        <div style={{ display: 'table-cell', width: '42%', textAlign: 'left' }}>Item</div>
        <div style={{ display: 'table-cell', width: '15%', textAlign: 'center' }}>Qty</div>
        <div style={{ display: 'table-cell', width: '20%', textAlign: 'right' }}>Rate</div>
        <div style={{ display: 'table-cell', width: '23%', textAlign: 'right' }}>Total</div>
      </div>

      {items && items.map((item, idx) => {
        const itemPriceWithVAT = Number(item.price) || Number(item.unit_price) || 0;
        const itemQty = Number(item.quantity) || 1;
        const itemTotalWithVAT = Number(item.subtotal) || (itemPriceWithVAT * itemQty);
        
        return (
          <div key={idx} style={{ display: 'table', width: '100%', padding: '4px 0', borderBottom: '1px dotted #ccc' }}>
            <div style={{ display: 'table-cell', width: '42%', textAlign: 'left', wordBreak: 'break-word', paddingRight: '2px' }}>
              {item.name || item.product_name}
            </div>
            <div style={{ display: 'table-cell', width: '15%', textAlign: 'center', verticalAlign: 'top' }}>{itemQty}</div>
            <div style={{ display: 'table-cell', width: '20%', textAlign: 'right', verticalAlign: 'top' }}>{itemPriceWithVAT.toFixed(2)}</div>
            <div style={{ display: 'table-cell', width: '23%', textAlign: 'right', verticalAlign: 'top' }}>{itemTotalWithVAT.toFixed(2)}</div>
          </div>
        );
      })}

      <div style={{ marginTop: '6px', paddingTop: '4px', borderTop: '1px solid #000' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Subtotal (excl. VAT):</span>
          <span>{subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>VAT ({actualVatRate}%):</span>
          <span>{vatAmount.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #000' }}>
          <span>TOTAL:</span>
          <span>{total.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '12px', paddingTop: '8px', borderTop: '1px dashed #000' }}>
        <div style={{ fontWeight: 'bold' }}>THANK YOU! VISIT AGAIN</div>
        <div style={{ fontSize: '9px', marginTop: '2px' }}>Quality is what we believe in</div>
      </div>
    </div>
  );
});

// ============================================================
// COMPONENT: A4BillPrint
// ============================================================

const A4BillPrint = React.forwardRef(({ bill, branch, vatRate, deliveryType }, ref) => {
  const subtotal = Number(bill.subtotal || bill.total_amount || 0);
  let vatAmount = Number(bill.vat || bill.vat_amount || 0);
  let total = Number(bill.final_amount || bill.grand_total || 0);
  const actualVatRate = bill.vat_rate || vatRate || 5;
  
  if (vatAmount === 0 && subtotal > 0) {
    vatAmount = subtotal * (actualVatRate / 100);
    total = subtotal + vatAmount;
  }
  
const logoUrl = 'https://nasmatalzahour.com/nursery/logo.png';
  //const logoUrl = window.location.origin + '/logo.jpg';
  const branchEmail = branch?.email || 'NasmatAlzohour@gmail.com';
  const branchPhone1 = '+971 552698951';
  const branchPhone2 = branch?.phone2 || '+971 526629183';
  const branchTrn = branch?.trn_no || 'TRN: 100220990400003';

  const formattedDate = bill.created_at 
    ? new Date(bill.created_at).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

  const customerNameEnglish = bill.customer_name || 'Guest';
  const customerNameArabic = transliterateToArabic(customerNameEnglish);
  const customerPhone = bill.customer_phone || '';
  const customerEmail = bill.customer_email || '';
  const customerAddress = bill.customer_address || '';
  const billItems = bill.items || bill.bill_items || [];

  return (
    <div ref={ref} style={{
      maxWidth: '800px',
      margin: '0 auto',
      background: 'white',
      fontFamily: "'Book Antiqua', 'Times New Roman', serif",
      padding: '30px',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ 
        marginBottom: '25px', 
        paddingBottom: '20px', 
        borderBottom: '2px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <div style={{ textAlign: 'center' }}>
            <img
              src={logoUrl}
              alt="Logo"
              style={{ width: '70px', height: 'auto' }}
              onError={(e) => (e.target.style.display = 'none')}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#2c4a3e', marginTop: '10px', textAlign: 'center' }}>NASMAT</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#c9a03d', marginTop: '10px' }}>AL ZOHOUR</div>
          </div>
        </div>
  
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000', letterSpacing: '2px', marginBottom: '10px' }}>TAX INVOICE</div>
          <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.5' }}>
            <div>{branch?.address || ''}</div>
            <div>📞 {branchPhone1}/{branchPhone2}</div>
            <div>Email: {branchEmail}</div>
            {branchTrn && <div>{branchTrn}</div>}
          </div>
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '30px',
        gap: '30px'
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontWeight: 'bold', 
            marginBottom: '10px', 
            fontSize: '14px',
            color: '#1e293b',
            paddingBottom: '5px'
          }}>Bill To</div>
          <div style={{ fontSize: '12px', color: '#475569' }}>
            <div><strong>{getValueOrNA(customerNameEnglish)}{customerNameArabic && ` / ${customerNameArabic}`}</strong></div>
            <div>Attn: {getValueOrNA(customerNameEnglish)}</div>
            <div>{customerAddress ? customerAddress.split(',')[0] : 'Client'}</div>
            {customerAddress && customerAddress.split(',')[1] && <div>{customerAddress.split(',')[1]}</div>}
            {customerPhone && <div>Phone: {customerPhone}</div>}
            {customerEmail && <div>Email: {customerEmail}</div>}
          </div>
        </div>

        <div style={{ minWidth: '250px', textAlign: 'right' }}>
          <div style={{ 
            fontWeight: 'bold', 
            marginBottom: '10px', 
            fontSize: '14px',
            color: '#1e293b',
            paddingBottom: '5px',
            textAlign: 'right'
          }}>Invoice Details</div>
          <div style={{ fontSize: '12px', lineHeight: '1.8', color: '#334155', textAlign: 'right' }}>
            <div><strong>Invoice No:</strong> {bill.bill_number || 'N/A'}</div>
            <div><strong>Date:</strong> {formattedDate}</div>
            {bill.delivery_type && (
              <div><strong>Delivery Type:</strong> {bill.delivery_type === 'delivery' ? ' Home Delivery' : ' Walk-in'}</div>
            )}
          </div>
        </div>
      </div>

      <table style={{ 
        width: '100%', 
        borderCollapse: 'collapse', 
        marginBottom: '25px',
        fontSize: '12px'
      }}>
        <thead>
          <tr style={{ 
            background: '#f8fafc', 
            borderTop: '1px solid #e2e8f0',
            borderBottom: '2px solid #e2e8f0'
          }}>
            <th style={{ padding: '10px', textAlign: 'left', width: '45%' }}>Description</th>
            <th style={{ padding: '10px', textAlign: 'center', width: '10%' }}>Qty</th>
            <th style={{ padding: '10px', textAlign: 'right', width: '20%' }}>Rate (AED incl. VAT)</th>
            <th style={{ padding: '10px', textAlign: 'right', width: '20%' }}>Total (AED)</th>
          </tr>
        </thead>
        <tbody>
          {billItems && billItems.length > 0 ? (
            billItems.map((item, idx) => {
              const itemPrice = item.unit_price || item.price || 0;
              const itemQty = item.quantity || 1;
              const itemTotal = item.subtotal || (itemPrice * itemQty);
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px' }}>
                    <strong>{item.name || item.product_name}</strong>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{itemQty}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>{itemPrice.toFixed(2)}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>{itemTotal.toFixed(2)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No items</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: '10px', paddingTop: '15px' }}>
        <div style={{ marginBottom: '5px', fontSize: '12px', textAlign: 'right' }}>
          Subtotal (excl. VAT): AED {subtotal.toFixed(2)}
        </div>
        <div style={{ marginBottom: '5px', fontSize: '12px', textAlign: 'right' }}>
          VAT ({actualVatRate}%): AED {vatAmount.toFixed(2)}
        </div>
        <div style={{ 
          marginTop: '10px', 
          paddingTop: '10px',
          borderTop: '2px solid #1e293b',
          fontSize: '16px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Payment Mode: {bill.payment_mode || 'Cash'}</span>
          <span>TOTAL AMOUNT: AED {total.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '10px', paddingTop: '10px' }}>
        <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '14px' }}>THANK YOU FOR SHOPPING WITH US!</p>
        <p style={{ fontSize: '10px', color: '#64748b', margin: '5px 0' }}>Quality is what we believe in</p>
      </div>
    </div>
  );
});

// ============================================================
// COMPONENT: ProductSearch
// ============================================================

const ProductSearch = ({ 
  searchTerm, 
  setSearchTerm, 
  searchSuggestions, 
  showSuggestions, 
  addToCart, 
  toggleFavorite, 
  favorites,
  getDisplayPrice,
  searchInputRef,
  searchProduct
}) => {
  return (
    <div style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        {/* BranchSelector will be passed as children */}
      </div>
      <div ref={searchInputRef} style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder="🔍 Search by product name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && searchProduct(searchTerm)}
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', outline: 'none', fontSize: '13px' }}
        />
        {showSuggestions && searchSuggestions.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', marginTop: '4px', maxHeight: '250px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {searchSuggestions.map(product => (
              <div
                key={product.id}
                onClick={() => { addToCart(product); setSearchTerm(''); }}
                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}
              >
                <span style={{ fontSize: '12px', fontWeight: '500' }}>{(product.name || '').toUpperCase()} {product.pot_size ? `(${product.pot_size})` : ''}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#64748b' }}>AED {getDisplayPrice(product).toFixed(2)}</span>
                  <button onClick={(e) => toggleFavorite(e, product.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px', fontSize: '14px' }}>
                    {favorites.includes(product.id) ? '⭐' : '☆'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: CategoryFilter
// ============================================================

const CategoryFilter = ({ categories, selectedCategory, setSelectedCategory, products, favorites }) => {
  return (
    <div style={{ position: 'relative', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px', padding: '0 4px' }}>
      <button
        onClick={() => {
          const container = document.querySelector('.category-scroll-container');
          if (container) container.scrollBy({ left: -200, behavior: 'smooth' });
        }}
        style={{
          width: '28px',
          height: '28px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#64748b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s',
          borderRadius: '4px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#4f46e5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
      >‹</button>

      <div 
        className="category-scroll-container"
        style={{ 
          flex: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          padding: '8px 0',
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
          cursor: 'grab'
        }}
        onMouseDown={(e) => {
          const container = e.currentTarget;
          let startX = e.pageX - container.offsetLeft;
          let scrollLeft = container.scrollLeft;
          const onMouseMove = (e) => {
            const x = e.pageX - container.offsetLeft;
            const walk = (x - startX) * 2;
            container.scrollLeft = scrollLeft - walk;
          };
          const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            container.style.cursor = 'grab';
          };
          container.style.cursor = 'grabbing';
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        }}
      >
        <div style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', padding: '0 4px' }}>
          <button 
            style={{ 
              padding: '6px 16px', 
              borderRadius: '20px', 
              border: 'none', 
              background: selectedCategory === 'All' ? '#64748b' : '#fff', 
              color: selectedCategory === 'All' ? '#fff' : '#64748b', 
              fontSize: '12px', 
              fontWeight: selectedCategory === 'All' ? '600' : '500', 
              cursor: 'pointer',
              boxShadow: selectedCategory === 'All' ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 2px rgba(0,0,0,0.05)',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }} 
            onClick={() => setSelectedCategory('All')}
          >All ({products.length})</button>
          
          {favorites.length > 0 && (
            <button 
              style={{ 
                padding: '6px 16px', 
                borderRadius: '20px', 
                border: 'none', 
                background: selectedCategory === '__favorites__' ? '#f59e0b' : '#fef3c7', 
                color: selectedCategory === '__favorites__' ? '#fff' : '#92400e', 
                fontSize: '12px', 
                fontWeight: selectedCategory === '__favorites__' ? '600' : '500', 
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }} 
              onClick={() => setSelectedCategory('__favorites__')}
            >⭐ Favourites ({favorites.length})</button>
          )}
          
          {categories.map(cat => {
            const categoryCount = products.filter(p => p.category === cat.cat_name).length;
            return (
              <button 
                key={cat.id} 
                style={{ 
                  padding: '6px 16px', 
                  borderRadius: '20px', 
                  border: 'none', 
                  background: selectedCategory === cat.cat_name ? '#64748b' : '#fff', 
                  color: selectedCategory === cat.cat_name ? '#fff' : '#64748b', 
                  fontSize: '12px', 
                  fontWeight: selectedCategory === cat.cat_name ? '600' : '500', 
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }} 
                onClick={() => setSelectedCategory(cat.cat_name)}
              >{cat.cat_name} ({categoryCount})</button>
            );
          })}
        </div>
      </div>

      <button
        onClick={() => {
          const container = document.querySelector('.category-scroll-container');
          if (container) container.scrollBy({ left: 200, behavior: 'smooth' });
        }}
        style={{
          width: '28px',
          height: '28px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#64748b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.2s',
          borderRadius: '4px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#4f46e5'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
      >›</button>
    </div>
  );
};

// ============================================================
// COMPONENT: ProductGrid
// ============================================================

const ProductGrid = ({ 
  products, 
  filteredProducts, 
  loading, 
  addToCart, 
  toggleFavorite, 
  favorites, 
  getDisplayPrice 
}) => {
  return (
    <div style={{ 
      flex: 1, 
      padding: '12px', 
      overflowY: 'auto', 
      minHeight: 0,
      msOverflowStyle: 'none',
      scrollbarWidth: 'none'
    }} className="products-grid-scroll">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading products...</div>
        ) : (
          filteredProducts.slice(0, 30).map(p => {
            const isOutOfStock = (p.prod_quantity || 0) <= 0;
            return (
              <div
                key={p.id}
                onClick={() => !isOutOfStock && addToCart(p)}
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '10px',
                  textAlign: 'center',
                  transition: 'all 0.2s',
                  cursor: isOutOfStock ? 'not-allowed' : 'pointer',
                  position: 'relative',
                  opacity: isOutOfStock ? 0.6 : 1
                }}
              >
                <button
                  onClick={(e) => toggleFavorite(e, p.id)}
                  style={{ position: 'absolute', top: '3px', right: '3px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', zIndex: 5 }}
                >{favorites.includes(p.id) ? '⭐' : '☆'}</button>
                <div style={{ fontSize: '11px', fontWeight: '600', marginBottom: '4px', color: '#1e293b' }}>
                  {(p.name || '').toUpperCase()} {p.pot_size ? `(${p.pot_size})` : ''}
                </div>
                <div><span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '20px', fontSize: '9px', color: '#64748b' }}>{(p.category || 'General').toUpperCase()}</span></div>
                <div style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', marginTop: '6px' }}>AED {getDisplayPrice(p).toFixed(2)}</div>
                <div style={{ borderTop: '1px solid #f1f5f9', marginTop: '6px', paddingTop: '6px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
                  {isOutOfStock ? (
                    <span style={{ fontSize: '9px', color: '#ef4444', fontWeight: '700' }}>OUT OF STOCK</span>
                  ) : (
                    <>
                      <span style={{ fontSize: '9px', color: '#64748b' }}>Stock:</span>
                      <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b' }}>{p.prod_quantity}</span>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: CartItems
// ============================================================

const CartItems = ({ 
  cart, 
  updateQuantity, 
  removeItem, 
  startNameEdit, 
  saveNameEdit, 
  editingNameId, 
  tempName, 
  setTempName, 
  handleNameKeyDown,
  startPriceEdit,
  editingPriceId,
  tempPrice,
  setTempPrice,
  savePriceEdit,
  priceInputRef,
  cartContainerRef
}) => {
  return (
    <div ref={cartContainerRef} style={{ 
      overflowY: 'auto', 
      padding: '10px',
      flex: '1 1 auto',
      minHeight: 0,
      msOverflowStyle: 'none',
      scrollbarWidth: 'none'
    }} className="cart-scroll">
      {cart.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: '40px', marginBottom: '8px' }}>🛒</div>
          <div style={{ fontSize: '12px' }}>Your cart is empty</div>
          <div style={{ fontSize: '10px', marginTop: '4px' }}>Click on products to add</div>
        </div>
      ) : (
        cart.map(i => (
          <div key={i.id} style={{ background: '#fafbfc', borderRadius: '8px', padding: '8px', marginBottom: '8px', border: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              {editingNameId === i.id ? (
                <input
                  type="text"
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onBlur={() => saveNameEdit(i.id)}
                  onKeyDown={(e) => handleNameKeyDown(e, i.id)}
                  style={{ fontSize: '12px', fontWeight: '500', color: '#1e293b', border: '1px solid #4f46e5', borderRadius: '4px', padding: '2px 6px', outline: 'none', flex: 1 }}
                  autoFocus
                />
              ) : (
                <span 
                  onDoubleClick={() => startNameEdit(i.id, i.name)}
                  style={{ fontSize: '12px', fontWeight: '500', color: '#1e293b', cursor: 'pointer', padding: '2px 0' }}
                  title="Double click to edit name"
                >{i.name}</span>
              )}
              <button onClick={() => removeItem(i.id)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', color: '#94a3b8', padding: '0 2px' }}>✕</button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button onClick={() => updateQuantity(i.id, -1)} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#64748b' }}>−</button>
                <span style={{ fontSize: '13px', fontWeight: '600', minWidth: '25px', textAlign: 'center' }}>{i.quantity}</span>
                <button onClick={() => updateQuantity(i.id, 1)} style={{ width: '24px', height: '24px', borderRadius: '4px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#64748b' }}>+</button>
              </div>
              <div style={{ textAlign: 'right' }}>
                {editingPriceId === i.id ? (
                  <input 
                    ref={priceInputRef} 
                    value={tempPrice} 
                    onChange={e => setTempPrice(e.target.value)} 
                    onBlur={() => savePriceEdit(i.id)} 
                    onKeyDown={e => e.key === 'Enter' && savePriceEdit(i.id)} 
                    style={{ width: '75px', textAlign: 'right', fontSize: '11px', padding: '2px', border: '1px solid #4f46e5', borderRadius: '4px', outline: 'none' }} 
                    autoFocus 
                  />
                ) : (
                  <div 
                    onDoubleClick={() => startPriceEdit(i.id, i.subtotal)} 
                    style={{ fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: '#1e293b' }}
                  >AED {Number(i.subtotal).toFixed(2)}</div>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ============================================================
// COMPONENT: DeliveryTypeSelector
// ============================================================

const DeliveryTypeSelector = ({ deliveryType, setDeliveryType, setCustomerAddress }) => {
  return (
    <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button 
          onClick={() => { setDeliveryType('walkin'); setCustomerAddress(''); }} 
          style={{ 
            flex: 1, 
            padding: '6px', 
            borderRadius: '6px', 
            border: deliveryType === 'walkin' ? '2px solid #4f46e5' : '1px solid #e2e8f0', 
            background: deliveryType === 'walkin' ? '#eef2ff' : '#fff', 
            fontSize: '11px', 
            fontWeight: deliveryType === 'walkin' ? '600' : '400', 
            cursor: 'pointer', 
            color: deliveryType === 'walkin' ? '#4f46e5' : '#64748b' 
          }}
        >🏪 Walk-in</button>
        <button 
          onClick={() => setDeliveryType('delivery')} 
          style={{ 
            flex: 1, 
            padding: '6px', 
            borderRadius: '6px', 
            border: deliveryType === 'delivery' ? '2px solid #4f46e5' : '1px solid #e2e8f0', 
            background: deliveryType === 'delivery' ? '#eef2ff' : '#fff', 
            fontSize: '11px', 
            fontWeight: deliveryType === 'delivery' ? '600' : '400', 
            cursor: 'pointer', 
            color: deliveryType === 'delivery' ? '#4f46e5' : '#64748b' 
          }}
        >🚚 Delivery</button>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: CustomerDetails
// ============================================================

const CustomerDetails = ({
  customerName,
  handleCustomerNameChange,
  customerPhone,
  handleCustomerPhoneChange,
  customerEmail,
  setCustomerEmail,
  customerAddress,
  setCustomerAddress,
  deliveryType,
  customers,
  showCustomerSuggestions,
  selectCustomer,
  errors,
  phoneExistsError,
  customerPhoneRef
}) => {
  return (
    <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
      <div ref={customerPhoneRef} style={{ position: 'relative', marginBottom: '6px' }}>
        <input 
          type="text" 
          placeholder="👤 Search by customer name or phone" 
          value={customerName} 
          onChange={(e) => handleCustomerNameChange(e.target.value)} 
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }} 
        />
        {showCustomerSuggestions && customers.length > 0 && !phoneExistsError && (
          <div style={{ 
            position: 'absolute', 
            top: '100%', 
            left: 0, 
            right: 0, 
            background: '#fff', 
            border: '1px solid #e2e8f0', 
            borderRadius: '6px', 
            maxHeight: '200px', 
            overflowY: 'auto', 
            zIndex: 1000, 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            marginTop: '2px'
          }}>
            {customers.map(c => (
              <div 
                key={c.id} 
                onClick={() => selectCustomer(c)} 
                style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{c.name}</div>
                {c.phone && <div style={{ fontSize: '11px', color: '#64748b' }}>📞 {c.phone}</div>}
                {c.email && <div style={{ fontSize: '10px', color: '#94a3b8' }}>✉️ {c.email}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input 
            type="tel" 
            placeholder="📞 Phone" 
            value={customerPhone} 
            onChange={(e) => handleCustomerPhoneChange(e.target.value)} 
            style={{ 
              width: '100%', 
              padding: '6px 10px', 
              fontSize: '12px', 
              border: (errors.customerPhone || phoneExistsError) ? '1px solid #ef4444' : '1px solid #e2e8f0', 
              borderRadius: '6px', 
              outline: 'none' 
            }} 
          />
          {phoneExistsError && (
            <div style={{ 
              position: 'absolute', 
              top: '100%', 
              left: 0, 
              right: 0, 
              background: '#fee2e2', 
              color: '#dc2626', 
              fontSize: '10px', 
              padding: '6px 8px', 
              borderRadius: '4px', 
              marginTop: '2px',
              zIndex: 100,
              border: '1px solid #fecaca',
              whiteSpace: 'normal',
              wordWrap: 'break-word'
            }}>{phoneExistsError}</div>
          )}
        </div>
        <input 
          type="email" 
          placeholder="✉️ Email" 
          value={customerEmail} 
          onChange={(e) => setCustomerEmail(e.target.value)} 
          style={{ flex: 1, padding: '6px 10px', fontSize: '12px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }} 
        />
      </div>
      
      {deliveryType === 'delivery' && (
        <textarea 
          placeholder="📍 Delivery address *" 
          value={customerAddress} 
          onChange={(e) => { setCustomerAddress(e.target.value); }} 
          rows="1" 
          style={{ width: '100%', padding: '6px 10px', fontSize: '12px', border: errors.customerAddress ? '1px solid #ef4444' : '1px solid #e2e8f0', borderRadius: '6px', resize: 'vertical', outline: 'none' }} 
        />
      )}
    </div>
  );
};

// ============================================================
// COMPONENT: PaymentSelector
// ============================================================

const PaymentSelector = ({ paymentMode, setPaymentMode, showPaymentDropdown, setShowPaymentDropdown, paymentRef }) => {
  return (
    <div ref={paymentRef} style={{ flex: 1, position: 'relative' }}>
      <button
        onClick={() => setShowPaymentDropdown(!showPaymentDropdown)}
        style={{
          width: '100%',
          padding: '8px 10px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          background: '#f1f5f9',
          fontSize: '11px',
          fontWeight: '500',
          cursor: 'pointer',
          textAlign: 'center',
          color: '#1e293b'
        }}
      >
        {paymentOptions.find(p => p.value === paymentMode)?.icon} {paymentOptions.find(p => p.value === paymentMode)?.label} ▼
      </button>
      {showPaymentDropdown && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '4px', zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          {paymentOptions.map(opt => (
            <div key={opt.value} onClick={() => { setPaymentMode(opt.value); setShowPaymentDropdown(false); }} style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '11px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {opt.icon} {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENT: OrderTotals
// ============================================================

const OrderTotals = ({
  subtotalExclVAT,
  vatAmount,
  vatRate,
  editingVat,
  tempVat,
  setTempVat,
  saveVatEdit,
  startVatEdit,
  grandTotal,
  vatInputRef
}) => {
  return (
    <div style={{ padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
        <span style={{ color: '#64748b' }}>Subtotal (excl. VAT)</span>
        <span style={{ fontWeight: '600', color: '#1e293b' }}>AED {subtotalExclVAT.toFixed(2)}</span>
      </div>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginBottom: '8px' }}>
        <span style={{ color: '#64748b' }}>
          VAT {editingVat ?
            <input ref={vatInputRef} value={tempVat} onChange={e => setTempVat(e.target.value)} onBlur={saveVatEdit} style={{ width: '45px', fontSize: '10px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px', marginLeft: '4px' }} /> :
            <span onDoubleClick={startVatEdit} style={{ cursor: 'pointer', background: '#f1f5f9', padding: '2px 8px', borderRadius: '20px', marginLeft: '4px', fontSize: '10px' }}>{vatRate}%</span>
          }
        </span>
        <span style={{ fontWeight: '600', color: '#1e293b' }}>AED {vatAmount.toFixed(2)}</span>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
        <PaymentSelector />
        <div style={{
          flex: 1,
          padding: '8px 10px',
          height: "auto",
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          background: '#f1f5f9',
          fontSize: '11px',
          fontWeight: '500',
          textAlign: 'center',
          color: '#1e293b'
        }}>
          <div style={{ fontSize: '9px', color: '#1e293b' }}>
            GRAND TOTAL : <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>AED {grandTotal.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: CreateOrderButton
// ============================================================

const CreateOrderButton = ({ 
  cart, 
  isProcessing, 
  phoneExistsError, 
  handleOrderSubmit 
}) => {
  return (
    <button
      onClick={handleOrderSubmit}
      disabled={cart.length === 0 || isProcessing || !!phoneExistsError}
      style={{
        width: '100%',
        padding: '10px',
        border: 'none',
        borderRadius: '6px',
        background: (cart.length === 0 || isProcessing || phoneExistsError) ? '#cbd5e1' : '#2563eb',
        color: '#fff',
        fontSize: '12px',
        fontWeight: '700',
        cursor: (cart.length === 0 || isProcessing || phoneExistsError) ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s'
      }}
    >
      {isProcessing ? '⏳ Processing...' : phoneExistsError ? '⚠️ Fix Mobile Number First' : '📝 CREATE ORDER'}
    </button>
  );
};

// ============================================================
// COMPONENT: PrintModal
// ============================================================

const PrintModal = ({ 
  showPrintModal, 
  lastCreatedBill, 
  currentOrderTotal, 
  paymentMode, 
  deliveryType, 
  setShowPrintModal, 
  printBill 
}) => {
  if (!showPrintModal || !lastCreatedBill) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '400px', maxWidth: '90%', overflow: 'hidden', animation: 'slideUp 0.3s ease' }}>
        <div style={{ padding: '25px', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: 'white', textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>✅</div>
          <h3 style={{ margin: '10px 0 5px', fontSize: '18px' }}>Order Created!</h3>
          <div style={{ fontSize: '13px' }}>Bill: <strong>{lastCreatedBill.bill_number}</strong></div>
        </div>
        <div style={{ padding: '25px' }}>
          <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '15px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span>Total:</span><strong>AED {currentOrderTotal.toFixed(2)}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}><span>Payment:</span><span>{paymentMode}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Delivery:</span><span>{deliveryType === 'delivery' ? 'Home Delivery 🚚' : 'Walk-in 🏪'}</span></div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '15px' }}>
            <button onClick={() => { printBill('thermal'); setShowPrintModal(false); }} style={{ flex: 1, padding: '12px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>🧾 80mm Print</button>
            <button onClick={() => { printBill('a4'); setShowPrintModal(false); }} style={{ flex: 1, padding: '12px', background: '#4f46e5', border: 'none', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>📄 A4 Print</button>
          </div>
          <button onClick={() => setShowPrintModal(false)} style={{ width: '100%', padding: '10px', background: '#f1f5f9', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: OrderManagement
// ============================================================

const OrderManagement = ({ allOrders, updatingStatus, updateDeliveryStatus, statusOptions }) => {
  const getStatusBadge = (status) => {
    const statusConfig = statusOptions.find(s => s.value === status) || statusOptions[0];
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '11px',
        fontWeight: '600',
        background: statusConfig.bg,
        color: statusConfig.color
      }}>
        {statusConfig.label}
      </span>
    );
  };

  return (
    <div style={{ margin: '15px', background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#1e293b' }}>📦 Delivery Orders</h3>
      <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
            <tr>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Bill #</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Customer</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Amount</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Status</th>
              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {allOrders.filter(order => order.delivery_type === 'delivery').map(order => (
              <tr key={order.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: '12px', fontWeight: '600' }}>{order.bill_number}</td>
                <td style={{ padding: '12px' }}>
                  {order.customer_name}<br />
                  <span style={{ fontSize: '11px', color: '#64748b' }}>{order.customer_phone}</span>
                </td>
                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>AED {Number(order.final_amount).toFixed(2)}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>{getStatusBadge(order.delivery_status)}</td>
                <td style={{ padding: '12px', textAlign: 'center' }}>
                  <select
                    value={order.delivery_status || 'Delivery Pending'}
                    onChange={(e) => updateDeliveryStatus(order.id, e.target.value)}
                    disabled={updatingStatus}
                    style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px', cursor: 'pointer', background: '#fff' }}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
            {allOrders.filter(order => order.delivery_type === 'delivery').length === 0 && (
              <tr>
                <td colSpan="5" style={{ padding: '50px', textAlign: 'center', color: '#94a3b8' }}>No delivery orders found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// MAIN POSBilling COMPONENT
// ============================================================

function POSBilling() {
  // ---- STATE ----
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBranch, setCurrentBranch] = useState(null);
  const [branches, setBranches] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [tempPrice, setTempPrice] = useState('');
  const [editingNameId, setEditingNameId] = useState(null);
  const [tempName, setTempName] = useState('');
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [vatRate, setVatRate] = useState(5);
  const [editingVat, setEditingVat] = useState(false);
  const [tempVat, setTempVat] = useState('');
  const [lastCreatedBill, setLastCreatedBill] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [currentOrderTotal, setCurrentOrderTotal] = useState(0);
  const [allOrders, setAllOrders] = useState([]);
  const [showOrderManagement, setShowOrderManagement] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('pos_favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [deliveryType, setDeliveryType] = useState('walkin');
  const [customers, setCustomers] = useState([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [errors, setErrors] = useState({});
  const [phoneExistsError, setPhoneExistsError] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // ---- REFS ----
  const thermalPrintRef = useRef(null);
  const a4PrintRef = useRef(null);
  const paymentRef = useRef(null);
  const cartContainerRef = useRef(null);
  const nameInputRef = useRef(null);
  const priceInputRef = useRef(null);
  const vatInputRef = useRef(null);
  const searchInputRef = useRef(null);
  const customerPhoneRef = useRef(null);

  // ---- EFFECTS ----
  useEffect(() => {
    localStorage.setItem('pos_favorites', JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
      setSearchSuggestions(filtered.slice(0, 6));
      setShowSuggestions(filtered.length > 0);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    fetchBranches();
    fetchCategories();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (currentBranch) {
      fetchProducts();
      fetchAllOrders();
      if (cart.length > 0) {
        setCart([]);
        resetCustomer();
      }
    }
  }, [currentBranch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchInputRef.current && !searchInputRef.current.contains(e.target)) setShowSuggestions(false);
      if (customerPhoneRef.current && !customerPhoneRef.current.contains(e.target)) setShowCustomerSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ---- API FUNCTIONS ----
  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_URL}/branches`);
      setBranches(res.data);
      const savedId = localStorage.getItem('selectedBranchId');
      if (savedId && res.data.length) setCurrentBranch(res.data.find(b => b.id === parseInt(savedId)) || res.data[0]);
      else if (res.data.length) setCurrentBranch(res.data[0]);
    } catch {
      setBranches([{ id: 1, branch_code: 'SHJ01', branch_name: 'NASMAT AL ZOHOUR - SHARJAH', address: 'Industrial Area, Sharjah', phone: '0552698951' }]);
      setCurrentBranch({ id: 1, branch_code: 'SHJ01', branch_name: 'NASMAT AL ZOHOUR - SHARJAH', address: 'Industrial Area, Sharjah', phone: '0552698951' });
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch {
      console.error('Error fetching categories');
    }
  };

  const fetchCustomers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/customers`);
      setCustomers(res.data);
    } catch (error) {
      console.error('Error fetching customers');
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!currentBranch) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/pos/plants`, { headers: { 'x-branch-id': currentBranch.id } });
      setProducts(res.data);
    } catch {
      alert('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [currentBranch]);

  const fetchAllOrders = useCallback(async () => {
    if (!currentBranch) return;
    try {
      const res = await axios.get(`${API_URL}/pos/bills`, {
        headers: { 'x-branch-id': currentBranch.id }
      });
      setAllOrders(res.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [currentBranch]);

  // ---- CUSTOMER FUNCTIONS ----
  const checkMobileExists = async (phone, excludeCustomerId = null) => {
    if (!phone || phone.trim() === '') return { exists: false, customer: null };
    try {
      const res = await axios.get(`${API_URL}/customers/check-mobile`, {
        params: { phone: phone.trim(), exclude_id: excludeCustomerId || '' }
      });
      return { exists: res.data.exists, customer: res.data.customer };
    } catch (err) {
      console.error('Error checking mobile:', err);
      return { exists: false, customer: null };
    }
  };

  const searchCustomers = async (searchValue) => {
    if (!searchValue || searchValue.trim() === '') {
      setShowCustomerSuggestions(false);
      return [];
    }
    try {
      const res = await axios.get(`${API_URL}/customers/search`, {
        params: { q: searchValue.trim() }
      });
      return res.data;
    } catch (err) {
      console.error('Error searching customers:', err);
      return [];
    }
  };

  const handleCustomerNameChange = async (value) => {
    setCustomerName(value);
    setSelectedCustomerId(null);
    setPhoneExistsError('');
    
    if (!value || value.trim() === '') {
      setShowCustomerSuggestions(false);
      setCustomers([]);
      return;
    }
    
    const searchResults = await searchCustomers(value);
    setCustomers(searchResults);
    setShowCustomerSuggestions(searchResults.length > 0);
  };

  const handleCustomerPhoneChange = async (value) => {
    setCustomerPhone(value);
    setSelectedCustomerId(null);
    setPhoneExistsError('');
    
    if (!value || value.trim() === '') {
      setPhoneExistsError('');
      setShowCustomerSuggestions(false);
      setCustomers([]);
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/customers/search`, {
        params: { q: value.trim() }
      });
      
      if (res.data && res.data.length > 0) {
        const exactMatch = res.data.find(c => c.phone === value);
        if (exactMatch) {
          setCustomerName(exactMatch.name || '');
          setCustomerEmail(exactMatch.email || '');
          setCustomerAddress(exactMatch.address || '');
          setSelectedCustomerId(exactMatch.id);
          setShowCustomerSuggestions(false);
          setPhoneExistsError('');
          setCustomers([]);
        } else {
          const checkRes = await axios.get(`${API_URL}/customers/check-mobile`, {
            params: { phone: value.trim() }
          });
          
          if (checkRes.data.exists && checkRes.data.customer) {
            setPhoneExistsError(`⚠️ Mobile number ${value} already belongs to "${checkRes.data.customer.name}". Please select that customer.`);
            setShowCustomerSuggestions(false);
            setCustomers([]);
          } else {
            setCustomers(res.data);
            setShowCustomerSuggestions(true);
          }
        }
      } else {
        setCustomers([]);
        setShowCustomerSuggestions(false);
      }
    } catch (err) {
      console.error('Error searching by phone:', err);
      setCustomers([]);
      setShowCustomerSuggestions(false);
    }
  };

  const selectCustomer = (customer) => {
    setCustomerName(customer.name || '');
    setCustomerPhone(customer.phone || '');
    setCustomerEmail(customer.email || '');
    setCustomerAddress(customer.address || '');
    setSelectedCustomerId(customer.id);
    setShowCustomerSuggestions(false);
    setErrors({});
    setPhoneExistsError('');
  };

  const resetCustomer = () => {
    setCustomerName('');
    setCustomerPhone('');
    setCustomerEmail('');
    setCustomerAddress('');
    setDeliveryType('walkin');
    setSelectedCustomerId(null);
    setErrors({});
    setPhoneExistsError('');
    setShowCustomerSuggestions(false);
  };

  const getOrCreateCustomer = async () => {
    if (!customerName.trim()) return null;

    let existingCustomer = null;

    if (selectedCustomerId) {
      existingCustomer = customers.find(c => c.id === selectedCustomerId);
    }
    
    if (!existingCustomer && customerPhone) {
      existingCustomer = customers.find(c => c.phone === customerPhone);
    }
    
    if (!existingCustomer && customerName) {
      existingCustomer = customers.find(c => c.name?.toLowerCase() === customerName.trim().toLowerCase());
    }

    if (existingCustomer) {
      if (customerPhone && existingCustomer.phone !== customerPhone) {
        const check = await checkMobileExists(customerPhone, existingCustomer.id);
        if (check.exists) {
          setPhoneExistsError(`⚠️ Mobile number ${customerPhone} already belongs to another customer`);
          throw new Error('Mobile number already exists');
        }
      }
      
      if ((customerPhone && existingCustomer.phone !== customerPhone) ||
          (customerEmail && existingCustomer.email !== customerEmail) ||
          (customerAddress && existingCustomer.address !== customerAddress)) {
        try {
          await axios.put(`${API_URL}/customers/${existingCustomer.id}`, {
            name: existingCustomer.name,
            phone: customerPhone || existingCustomer.phone,
            email: customerEmail || existingCustomer.email,
            address: customerAddress || existingCustomer.address
          });
          await fetchCustomers();
        } catch (err) {
          console.error('Error updating customer:', err);
          if (err.response?.status === 409) {
            setPhoneExistsError('⚠️ This mobile number is already registered');
            throw new Error('Mobile number already exists');
          }
        }
      }
      return existingCustomer;
    }

    if (customerPhone) {
      const check = await checkMobileExists(customerPhone, null);
      if (check.exists) {
        setPhoneExistsError(`⚠️ Mobile number ${customerPhone} already exists`);
        throw new Error('Mobile number already exists');
      }
    }

    try {
      const customerData = {
        name: customerName.trim(),
        phone: customerPhone || '',
        email: customerEmail || '',
        address: customerAddress || ''
      };
      const res = await axios.post(`${API_URL}/customers`, customerData);
      setSelectedCustomerId(res.data.id);
      await fetchCustomers();
      setPhoneExistsError('');
      return res.data;
    } catch (error) {
      console.error('Error creating customer:', error);
      if (error.response?.status === 409) {
        setPhoneExistsError('⚠️ This mobile number is already registered. Please use a different number.');
        throw new Error('Mobile number already exists');
      }
      throw error;
    }
  };

  const validateCustomer = () => {
    const newErrors = {};
    if (customerName.trim() && !customerPhone.trim()) {
      newErrors.customerPhone = 'Phone number is required';
    } else if (customerPhone && !/^\d{10,15}$/.test(customerPhone.replace(/[^0-9]/g, ''))) {
      newErrors.customerPhone = 'Enter valid phone number';
    }
    if (deliveryType === 'delivery' && !customerAddress.trim()) {
      newErrors.customerAddress = 'Delivery address is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- CART FUNCTIONS ----
  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id);
      const availableStock = product.prod_quantity || 0;
      const currentInCart = existing ? existing.quantity : 0;

      if (availableStock > 0 && currentInCart >= availableStock) {
        alert('Out of stock!');
        return prev;
      }

      const basePrice = Number(product.price);
      const priceWithVAT = getPriceWithVAT(basePrice, vatRate);
      const displayName = `${(product.name || '').toUpperCase()} ${product.pot_size ? `(${product.pot_size})` : ''}`;
      
      if (existing) {
        const newQuantity = existing.quantity + 1;
        const newSubtotalWithVAT = roundToTwo(newQuantity * existing.price_with_vat);
        return prev.map(i => i.id === product.id ? { 
          ...i, 
          quantity: newQuantity, 
          subtotal: newSubtotalWithVAT
        } : i);
      }
      
      return [{ 
        ...product, 
        name: displayName, 
        quantity: 1, 
        base_price: basePrice,
        price_with_vat: priceWithVAT,
        price: priceWithVAT,
        subtotal: priceWithVAT,
        vat_rate: product.vat_rate || vatRate 
      }, ...prev];
    });
  }, [vatRate]);

  const updateQuantity = useCallback((id, delta) => {
    setCart(prev => {
      const item = prev.find(i => i.id === id);
      if (!item) return prev;

      const product = products.find(p => p.id === id);
      const availableStock = product?.prod_quantity || 0;

      if (delta > 0 && availableStock > 0 && item.quantity >= availableStock) {
        alert('Out of stock!');
        return prev;
      }

      const newQty = item.quantity + delta;
      if (newQty < 1) return prev.filter(i => i.id !== id);
      const newSubtotalWithVAT = roundToTwo(newQty * item.price_with_vat);
      return prev.map(i => i.id === id ? { 
        ...i, 
        quantity: newQty, 
        subtotal: newSubtotalWithVAT
      } : i);
    });
  }, [products]);

  const removeItem = useCallback((id) => { 
    setCart(prev => prev.filter(i => i.id !== id)); 
  }, []);

  const startNameEdit = (id, currentName) => {
    setEditingNameId(id);
    setTempName(currentName);
  };

  const saveNameEdit = (id) => {
    if (tempName.trim()) {
      setCart(prev => prev.map(i => 
        i.id === id ? { ...i, name: tempName.trim() } : i
      ));
    }
    setEditingNameId(null);
    setTempName('');
  };

  const handleNameKeyDown = (e, id) => {
    if (e.key === 'Enter') {
      saveNameEdit(id);
    } else if (e.key === 'Escape') {
      setEditingNameId(null);
      setTempName('');
    }
  };

  const startPriceEdit = (id, subtotal) => { 
    setEditingPriceId(id); 
    setTempPrice(subtotal.toString()); 
  };

  const savePriceEdit = async (id) => {
    const newTotal = roundToTwo(parseFloat(tempPrice));
    if (!isNaN(newTotal) && newTotal >= 0) {
      try {
        const item = cart.find(i => i.id === id);
        if (!item) return;
        const newUnitPriceWithVAT = item.quantity > 0 ? roundToTwo(newTotal / item.quantity) : newTotal;
        const newBasePrice = roundToTwo(newUnitPriceWithVAT / (1 + vatRate / 100));

        await axios.patch(`${API_URL}/plants/${id}/price`, { price: newBasePrice });

        setCart(prev => prev.map(i => i.id === id ? { 
          ...i, 
          base_price: newBasePrice,
          price_with_vat: newUnitPriceWithVAT,
          price: newUnitPriceWithVAT, 
          subtotal: newTotal 
        } : i));
        setProducts(prev => prev.map(p => p.id === id ? { ...p, price: newBasePrice, retail_price: newBasePrice } : p));
      } catch (err) {
        console.error('Error syncing price:', err);
        alert('Failed to sync master price.');
      }
    }
    setEditingPriceId(null);
  };

  // ---- VAT FUNCTIONS ----
  const startVatEdit = () => { 
    setEditingVat(true); 
    setTempVat(vatRate.toString()); 
  };

  const saveVatEdit = () => {
    const newVat = parseFloat(tempVat);
    if (!isNaN(newVat) && newVat >= 0 && newVat <= 100) setVatRate(newVat);
    setEditingVat(false);
  };

  // ---- FAVORITES ----
  const toggleFavorite = (e, id) => {
    e.stopPropagation();
    setFavorites(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
  };

  // ---- ORDER FUNCTIONS ----
  const processOrder = async () => {
    if (cart.length === 0) { 
      alert('Cart is empty'); 
      return false; 
    }
    if (!currentBranch) { 
      alert('Select branch'); 
      return false; 
    }
    
    setPhoneExistsError('');
    
    if (!validateCustomer()) {
      const errorMessages = Object.values(errors).join('\n');
      alert(`Please fix:\n${errorMessages}`);
      return false;
    }
    
    if (isProcessing) return false;

    setIsProcessing(true);
    try {
      let finalCustomerId = selectedCustomerId;
      let finalCustomerName = customerName.trim();
      let finalCustomerPhone = customerPhone;
      let finalCustomerEmail = customerEmail;
      let finalCustomerAddress = deliveryType === 'delivery' ? customerAddress : '';

      if (finalCustomerName) {
        try {
          const customer = await getOrCreateCustomer();
          if (customer) {
            finalCustomerId = customer.id;
            finalCustomerName = customer.name;
            finalCustomerPhone = customer.phone || '';
            finalCustomerEmail = customer.email || '';
          }
        } catch (err) {
          if (err.message === 'Mobile number already exists') {
            alert(phoneExistsError || '⚠️ This mobile number is already registered. Please use a different number or search for the existing customer.');
            setIsProcessing(false);
            return false;
          }
          throw err;
        }
      }

      const subtotalExclVAT = getSubtotalExclVAT(grandTotal, vatRate);
      const vatAmount = getVATAmount(grandTotal, vatRate);

      const billData = {
        items: cart.map(i => ({
          product_id: i.id,
          name: i.name,
          quantity: i.quantity,
          price: i.base_price,
          subtotal: i.subtotal
        })),
        total_amount: subtotalExclVAT,
        vat_amount: vatAmount,
        final_amount: grandTotal,
        customer_id: finalCustomerId || null,
        customer_name: finalCustomerName || 'Guest',
        customer_phone: finalCustomerPhone || '',
        customer_email: finalCustomerEmail || '',
        customer_address: finalCustomerAddress || '',
        delivery_type: deliveryType,
        payment_mode: paymentMode,
        vat_rate: vatRate,
        delivery_status: deliveryType === 'delivery' ? 'Delivery Pending' : 'Completed'
      };

      const res = await axios.post(`${API_URL}/pos/bills`, billData, { 
        headers: { 'x-branch-id': currentBranch.id } 
      });
      
      localStorage.setItem('selectedBranchId', currentBranch.id);
      
      const newBill = {
        ...res.data,
        items: [...cart],
        customer_name: finalCustomerName || 'Guest',
        customer_phone: finalCustomerPhone,
        customer_email: finalCustomerEmail,
        customer_address: finalCustomerAddress,
        delivery_type: deliveryType,
        payment_mode: paymentMode,
        subtotal: subtotalExclVAT,
        branch_details: currentBranch,
        vat_rate: vatRate,
        delivery_status: deliveryType === 'delivery' ? 'Delivery Pending' : 'Completed'
      };

      setLastCreatedBill(newBill);
      setCurrentOrderTotal(grandTotal);
      setShowPrintModal(true);
      setCart([]);
      fetchProducts();
      fetchAllOrders();
      resetCustomer();

      return true;
    } catch (error) {
      console.error(error);
      if (error.response?.data?.error) {
        alert(`❌ Customer Error: ${error.response.data.error}`);
      } else if (error.response?.data?.message) {
        alert(`❌ Customer Error: ${error.response.data.message}`);
      } else {
        alert(`❌ Customer Error: ${error.message || 'Unknown customer error'}`);
      }
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOrderSubmit = () => {
    if (cart.length === 0) {
      alert('Cart is empty');
      return;
    }
    processOrder();
  };

  const updateDeliveryStatus = async (orderId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API_URL}/transactions/bills/${orderId}/delivery-status`, {
        delivery_status: newStatus
      });
      await fetchAllOrders();
      alert(`Order status updated to ${newStatus}`);
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleBranchChange = (branch) => {
    if (cart.length > 0 && !window.confirm('Changing branch will clear your cart. Continue?')) return;
    setCart([]);
    resetCustomer();
    setCurrentBranch(branch);
    localStorage.setItem('selectedBranchId', branch.id);
  };

  const searchProduct = async (searchText) => {
    if (!searchText.trim() || !currentBranch) return;
    let found = products.find(p => p.name.toLowerCase().includes(searchText.toLowerCase()));
    if (found) {
      addToCart(found);
      setSearchTerm('');
      setShowSuggestions(false);
    } else if (!found) {
      alert('Product not found');
    }
  };

  const printBill = (type) => {
    if (!lastCreatedBill) {
      alert('No bill to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    const printContent = type === 'thermal'
      ? <ThermalBillPrint bill={lastCreatedBill} branch={currentBranch} vatRate={vatRate} deliveryType={deliveryType} />
      : <A4BillPrint bill={lastCreatedBill} branch={currentBranch} vatRate={vatRate} deliveryType={deliveryType} />;

    const container = document.createElement('div');
    const root = ReactDOM.createRoot(container);
    root.render(printContent);

    const thermalStyles = `
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 80mm !important;
          background: white !important;
        }
        @page { 
          size: 80mm auto; 
          margin: 0mm !important; 
        }
      }
      * {
        font-family: monospace !important;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeSpeed; 
      }
    `;

    const a4Styles = `
      @media print {
        body { margin: 20mm; padding: 0; }
        @page { size: auto; margin: auto; }
      }
    `;

    setTimeout(() => {
      const html = container.innerHTML;
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice - ${lastCreatedBill.bill_number || 'Bill'}</title>
            <style>
              ${type === 'thermal' ? thermalStyles : a4Styles}
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.close();
                }, 300);
              };
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
      root.unmount();
    }, 400);
  };

  // ---- COMPUTED VALUES ----
  const grandTotal = roundToTwo(cart.reduce((s, i) => s + Number(i.subtotal), 0));
  const vatAmount = getVATAmount(grandTotal, vatRate);
  const subtotalExclVAT = getSubtotalExclVAT(grandTotal, vatRate);
  const filteredProducts = selectedCategory === 'All'
    ? products
    : selectedCategory === '__favorites__'
      ? products.filter(p => favorites.includes(p.id))
      : products.filter(p => p.category === selectedCategory);
  const getDisplayPrice = (product) => getPriceWithVAT(Number(product.price), vatRate);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={{ height: '90vh', background: '#f0f2f5', fontFamily: "'Book Antiqua', 'Times New Roman', serif", display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      
      {/* Order Management Panel */}
      {showOrderManagement && (
        <OrderManagement 
          allOrders={allOrders}
          updatingStatus={updatingStatus}
          updateDeliveryStatus={updateDeliveryStatus}
          statusOptions={statusOptions}
        />
      )}

      {/* Main POS Content */}
      <div style={{
        display: 'flex',
        flexDirection: window.innerWidth < 1024 ? 'column' : 'row',
        gap: '15px',
        padding: '15px',
        flex: 1,
        overflow: 'hidden',
        minHeight: 0
      }}>
        
        {/* LEFT PANEL - PRODUCTS */}
        <div style={{ flex: 2.5, background: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', height: 'calc(100vh - 100px)' }}>
          
          {/* Search Bar */}
          <div style={{ padding: '12px 15px', borderBottom: '1px solid #e9ecef', flexShrink: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1e293b' }}>Products</h3>
              <BranchSelector currentBranch={currentBranch} onBranchChange={handleBranchChange} branches={branches} />
            </div>
            <ProductSearch 
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              searchSuggestions={searchSuggestions}
              showSuggestions={showSuggestions}
              addToCart={addToCart}
              toggleFavorite={toggleFavorite}
              favorites={favorites}
              getDisplayPrice={getDisplayPrice}
              searchInputRef={searchInputRef}
              searchProduct={searchProduct}
            />
          </div>

          {/* Categories Filter */}
          <CategoryFilter 
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            products={products}
            favorites={favorites}
          />

          {/* Products Grid */}
          <ProductGrid 
            products={products}
            filteredProducts={filteredProducts}
            loading={loading}
            addToCart={addToCart}
            toggleFavorite={toggleFavorite}
            favorites={favorites}
            getDisplayPrice={getDisplayPrice}
          />
        </div>

        {/* RIGHT PANEL - CART */}
        <div style={{ 
          width: window.innerWidth < 1024 ? '100%' : '420px', 
          background: '#fff', 
          borderRadius: '12px', 
          border: '1px solid #e2e8f0', 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden', 
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          height: 'calc(100vh - 100px)'
        }}>
          
          {/* Header */}
          <div style={{ padding: '12px 15px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff', flexShrink: 0 }}>
            <span style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>🛒 Shopping Cart</span>
            <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', color: '#64748b' }}>{cart.length} items</span>
          </div>

          {/* Cart Items */}
          <CartItems 
            cart={cart}
            updateQuantity={updateQuantity}
            removeItem={removeItem}
            startNameEdit={startNameEdit}
            saveNameEdit={saveNameEdit}
            editingNameId={editingNameId}
            tempName={tempName}
            setTempName={setTempName}
            handleNameKeyDown={handleNameKeyDown}
            startPriceEdit={startPriceEdit}
            editingPriceId={editingPriceId}
            tempPrice={tempPrice}
            setTempPrice={setTempPrice}
            savePriceEdit={savePriceEdit}
            priceInputRef={priceInputRef}
            cartContainerRef={cartContainerRef}
          />

          {/* Fixed Bottom Section */}
          <div style={{ flexShrink: 0, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
            
            {/* Delivery Type */}
            <DeliveryTypeSelector 
              deliveryType={deliveryType}
              setDeliveryType={setDeliveryType}
              setCustomerAddress={setCustomerAddress}
            />

            {/* Customer Details */}
            <CustomerDetails 
              customerName={customerName}
              handleCustomerNameChange={handleCustomerNameChange}
              customerPhone={customerPhone}
              handleCustomerPhoneChange={handleCustomerPhoneChange}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerAddress={customerAddress}
              setCustomerAddress={setCustomerAddress}
              deliveryType={deliveryType}
              customers={customers}
              showCustomerSuggestions={showCustomerSuggestions}
              selectCustomer={selectCustomer}
              errors={errors}
              phoneExistsError={phoneExistsError}
              customerPhoneRef={customerPhoneRef}
            />

            {/* Totals */}
            <div style={{ padding: '10px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                <span style={{ color: '#64748b' }}>Subtotal (excl. VAT)</span>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>AED {subtotalExclVAT.toFixed(2)}</span>
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', marginBottom: '8px' }}>
                <span style={{ color: '#64748b' }}>
                  VAT {editingVat ?
                    <input ref={vatInputRef} value={tempVat} onChange={e => setTempVat(e.target.value)} onBlur={saveVatEdit} style={{ width: '45px', fontSize: '10px', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '2px', marginLeft: '4px' }} /> :
                    <span onDoubleClick={startVatEdit} style={{ cursor: 'pointer', background: '#f1f5f9', padding: '2px 8px', borderRadius: '20px', marginLeft: '4px', fontSize: '10px' }}>{vatRate}%</span>
                  }
                </span>
                <span style={{ fontWeight: '600', color: '#1e293b' }}>AED {vatAmount.toFixed(2)}</span>
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <PaymentSelector 
                  paymentMode={paymentMode}
                  setPaymentMode={setPaymentMode}
                  showPaymentDropdown={showPaymentDropdown}
                  setShowPaymentDropdown={setShowPaymentDropdown}
                  paymentRef={paymentRef}
                />
                <div style={{
                  flex: 1,
                  padding: '8px 10px',
                  height: "auto",
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  background: '#f1f5f9',
                  fontSize: '11px',
                  fontWeight: '500',
                  textAlign: 'center',
                  color: '#1e293b'
                }}>
                  <div style={{ fontSize: '9px', color: '#1e293b' }}>
                    GRAND TOTAL : <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e293b' }}>AED {grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <CreateOrderButton 
                cart={cart}
                isProcessing={isProcessing}
                phoneExistsError={phoneExistsError}
                handleOrderSubmit={handleOrderSubmit}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Print Modal */}
      <PrintModal 
        showPrintModal={showPrintModal}
        lastCreatedBill={lastCreatedBill}
        currentOrderTotal={currentOrderTotal}
        paymentMode={paymentMode}
        deliveryType={deliveryType}
        setShowPrintModal={setShowPrintModal}
        printBill={printBill}
      />

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Hide scrollbars */
        .category-scroll-container::-webkit-scrollbar,
        .products-grid-scroll::-webkit-scrollbar,
        .cart-scroll::-webkit-scrollbar {
          display: none;
        }
        
        .category-scroll-container,
        .products-grid-scroll,
        .cart-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </div>
  );
}

export default POSBilling;
